import type { ValueOf } from '@sel/utils';
import { z } from 'zod';

import { MembersSort } from './members-sort';

import type { Address } from './address';
import type { MemberInterest } from './interest';

export const MemberStatus = {
  onboarding: 'onboarding',
  inactive: 'inactive',
  active: 'active',
  system: 'system',
} as const;

export type MemberStatus = ValueOf<typeof MemberStatus>;

export const MemberRole = {
  member: 'member',
  admin: 'admin',
  committee: 'committee',
  system: 'system',
} as const;

export type MemberRole = ValueOf<typeof MemberRole>;

export const ProfileStep = {
  name: 'name',
  contactInfo: 'contactInfo',
  contactVisibility: 'contactVisibility',
  notifications: 'notifications',
  address: 'address',
  avatar: 'avatar',
  bio: 'bio',
  interests: 'interests',
  installApp: 'installApp',
  help: 'help',
} as const;

export type ProfileStep = ValueOf<typeof ProfileStep>;

export type Member = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  email?: string;
  phoneNumber?: string;
  bio?: string;
  address?: Address;
  avatar?: string;
  membershipStartDate: string;
  balance: number;
  interests: MemberInterest[];
  committeeMember: boolean;
};

export type LightMember = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  avatar?: string;
};

export type MemberTransactionStats = {
  given: number;
  received: number;
  count: number;
  partners: number;
};

export const listMembersQuerySchema = z.object({
  sort: z.nativeEnum(MembersSort).optional(),
});

export const createMemberBodySchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string(),
});

export const updateMemberProfileBodySchema = z.object({
  firstName: z.string().trim().min(1).max(256).optional(),
  lastName: z.string().trim().min(1).max(256).optional(),
  emailVisible: z.boolean().optional(),
  phoneNumber: z
    .string()
    .regex(/^0\d{9}$/)
    .optional(),
  phoneNumberVisible: z.boolean().optional(),
  bio: z.string().trim().max(4096).nullable().optional(),
  address: z
    .object({
      line1: z.string().trim().max(256),
      line2: z.string().trim().max(256).optional(),
      postalCode: z.string().trim().max(16),
      city: z.string().trim().max(256),
      country: z.string().trim().max(256),
      position: z.tuple([z.number(), z.number()]).optional(),
    })
    .nullable()
    .optional(),
  avatarFileName: z.string().nullable().optional(),
  onboardingCompleted: z.boolean().optional(),
  completedProfileSteps: z.array(z.enum(ProfileStep)).optional(),
});

export type UpdateMemberProfileData = z.infer<typeof updateMemberProfileBodySchema>;

export const notificationDeliveryBodySchema = z.object({
  email: z.boolean(),
  push: z.boolean(),
});
